import React, { useState } from "react";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCard,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
  IonIcon,
  IonLoading,
  IonRefresher,
  IonRefresherContent,
  IonText,
  IonButton,
  IonButtons,
  IonSegment,
  IonSegmentButton,
} from "@ionic/react";
import {
  trophyOutline,
  peopleOutline,
  starOutline,
  medalOutline,
  chevronDownOutline,
  chevronUpOutline,
  expandOutline,
  contractOutline,
} from "ionicons/icons";
import { useQuery } from "react-query";
import api from "@/services/api";
import { useAuthStore } from "@/store/authStore";
import { useI18n } from "@/i18n";

interface LeagueStanding {
  playerId: number;
  username: string;
  displayName: string;
  totalPoints: number;
  matchesPredicted: number;
  exactScores: number;
  goalDifferences: number;
  tendencies: number;
  position: number;
}

interface League {
  id: number;
  name: string;
  tier: number;
  division: string;
  invite_code: string;
  max_members: number;
  season_name: string;
  current_members: number;
  standings: LeagueStanding[];
  total_standings: number;
}

interface AllLeaguesResponse { leagues: League[]; total_leagues: number; }
interface Round { id: number; round_number: number; start_matchday: number; end_matchday: number; status: string; promotion_relegation_completed: boolean; created_at: string; }
interface RoundLeaguesResponse { round: Round; leagues: League[]; total_leagues: number; }
interface RoundsResponse { rounds: Round[]; total_rounds: number; }

const rankClass = (pos: number) => {
  if (pos === 1) return "rank-badge rank-1";
  if (pos === 2) return "rank-badge rank-2";
  if (pos === 3) return "rank-badge rank-3";
  return "rank-badge rank-default";
};

const getTierIcon = (tier: number) => {
  switch (tier) { case 1: return trophyOutline; case 2: return medalOutline; case 3: return starOutline; default: return peopleOutline; }
};

const AllLeagues: React.FC = () => {
  const [expandedLeagues, setExpandedLeagues] = useState<Set<number>>(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  const [selectedRound, setSelectedRound] = useState<string>("current");
  const { player } = useAuthStore();
  const { t } = useI18n();

  const { data: roundsData } = useQuery<RoundsResponse>(["rounds"], async () => (await api.get("/api/leagues/rounds")).data);

  const { data: leaguesData, isLoading: leaguesLoading, error, refetch } = useQuery<AllLeaguesResponse>(
    ["all-leagues"],
    async () => (await api.get("/api/leagues/all")).data,
    { refetchInterval: 30000, enabled: selectedRound === "current" }
  );

  const { data: roundLeaguesData, isLoading: roundLeaguesLoading, error: roundLeaguesError, refetch: refetchRoundLeagues } = useQuery<RoundLeaguesResponse>(
    ["round-leagues", selectedRound],
    async () => (await api.get(`/api/leagues/round/${selectedRound}`)).data,
    { enabled: selectedRound !== "current" && selectedRound !== "" }
  );

  const handleRefresh = async (event: CustomEvent) => {
    if (selectedRound === "current") await refetch(); else await refetchRoundLeagues();
    event.detail.complete();
  };

  const toggleLeague = (id: number) => {
    const next = new Set(expandedLeagues);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpandedLeagues(next);
  };

  const getCurrentLeagues = (): League[] => selectedRound === "current" ? leaguesData?.leagues || [] : roundLeaguesData?.leagues || [];

  const expandAll = () => { setExpandedLeagues(new Set(getCurrentLeagues().map((l) => l.id))); setAllExpanded(true); };
  const collapseAll = () => { setExpandedLeagues(new Set()); setAllExpanded(false); };

  const getCurrentRoundInfo = () => roundsData?.rounds.find((r) => r.status === "active") || null;
  const getRoundStatusLabel = (status: string) => { switch (status) { case "active": return t("active"); case "completed": return t("completed"); case "upcoming": return t("upcoming"); default: return status; } };

  const isLoading = leaguesLoading || roundLeaguesLoading;
  const currentError = selectedRound === "current" ? error : roundLeaguesError;

  if (isLoading) {
    return (
      <IonPage>
        <IonHeader><IonToolbar><IonTitle>{t("allLeaguesTitle")}</IonTitle></IonToolbar></IonHeader>
        <IonContent><IonLoading isOpen message={t("loadingLeagues")} /></IonContent>
      </IonPage>
    );
  }

  if (currentError) {
    return (
      <IonPage>
        <IonHeader><IonToolbar><IonTitle>{t("allLeaguesTitle")}</IonTitle></IonToolbar></IonHeader>
        <IonContent className="ion-padding"><IonText color="danger"><p>{t("errorLoadingLeagues")}</p></IonText></IonContent>
      </IonPage>
    );
  }

  const leagues = getCurrentLeagues();
  const roundInfo = getCurrentRoundInfo();

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t("allLeaguesTitle")}</IonTitle>
          <IonButtons slot="end">
            <IonButton fill="clear" onClick={allExpanded ? collapseAll : expandAll}>
              <IonIcon icon={allExpanded ? contractOutline : expandOutline} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}><IonRefresherContent /></IonRefresher>

        <div style={{ padding: "12px 16px" }}>
          <p style={{ margin: "0 0 4px", color: "var(--ion-color-medium)", fontSize: "13px" }}>
            {leagues.length} {t("leaguesInSystem")}
            {roundInfo && ` · ${t("roundLabel")} ${roundInfo.round_number} (${t("matchdaysLabel")} ${roundInfo.start_matchday}-${roundInfo.end_matchday}) – ${getRoundStatusLabel(roundInfo.status)}`}
          </p>

          {roundsData && roundsData.rounds.length > 0 && (
            <IonSegment value={selectedRound} onIonChange={(e) => setSelectedRound(e.detail.value as string)} style={{ marginBottom: "12px" }}>
              <IonSegmentButton value="current"><IonLabel>{t("currentLabel")}</IonLabel></IonSegmentButton>
              {roundsData.rounds.filter((r) => r.status === "completed").map((r) => (
                <IonSegmentButton key={r.id} value={r.id.toString()}><IonLabel>{t("roundLabel")} {r.round_number}</IonLabel></IonSegmentButton>
              ))}
            </IonSegment>
          )}

          {leagues.map((league) => {
            const isExpanded = expandedLeagues.has(league.id);
            const tierColor = league.tier === 1 ? "var(--app-gold)" : league.tier === 2 ? "var(--app-silver)" : "var(--app-bronze)";
            return (
              <IonCard key={league.id} style={{ margin: "0 0 10px 0" }}>
                {/* Header with green gradient strip */}
                <div
                  onClick={() => toggleLeague(league.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: "10px",
                    padding: "12px 14px", cursor: "pointer",
                    background: `linear-gradient(90deg, ${tierColor}22 0%, transparent 100%)`,
                    borderBottom: isExpanded ? "1px solid var(--ion-border-color)" : "none",
                  }}
                >
                  <IonIcon icon={getTierIcon(league.tier)} style={{ fontSize: "22px", color: tierColor }} />
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: "14px" }}>{league.name}</strong>
                    <div style={{ fontSize: "11px", color: "var(--ion-color-medium)" }}>
                      {league.current_members}/{league.max_members} {t("membersLabel")}
                    </div>
                  </div>
                  <IonBadge style={{ "--background": tierColor, fontSize: "10px", color: league.tier === 1 ? "#000" : "#fff" }}>
                    {t("tierLabel")} {league.tier}
                  </IonBadge>
                  <IonIcon icon={isExpanded ? chevronUpOutline : chevronDownOutline} style={{ fontSize: "18px", color: "var(--ion-color-medium)" }} />
                </div>

                {isExpanded && (
                  <IonCardContent style={{ padding: 0 }}>
                    {league.standings.length > 0 ? (
                      <IonList lines="full" style={{ padding: 0 }}>
                        {league.standings.map((s) => {
                          const isCurrent = player && s.playerId === player.id;
                          return (
                            <IonItem key={s.playerId} className={isCurrent ? "player-highlight" : ""} style={{ "--min-height": "48px" }}>
                              <div slot="start" className={rankClass(s.position)}>{s.position}</div>
                              <IonLabel>
                                <h3 style={{ fontWeight: isCurrent ? 700 : 500, fontSize: "13px" }}>
                                  {s.displayName || s.username}
                                </h3>
                                <p className="egt-text" style={{ color: "var(--ion-color-medium)" }}>
                                  <span style={{ color: "var(--app-score-exact)" }}>{s.exactScores}E</span>{" "}
                                  <span style={{ color: "var(--app-score-diff)" }}>{s.goalDifferences}G</span>{" "}
                                  <span style={{ color: "var(--app-score-tendency)" }}>{s.tendencies}T</span>
                                </p>
                              </IonLabel>
                              <div slot="end" className="tabular-nums" style={{ fontWeight: 700, fontSize: "14px" }}>
                                {s.totalPoints}
                              </div>
                            </IonItem>
                          );
                        })}
                      </IonList>
                    ) : (
                      <p style={{ padding: "16px", color: "var(--ion-color-medium)", fontSize: "13px" }}>{t("noPlayersInLeague")}</p>
                    )}
                  </IonCardContent>
                )}
              </IonCard>
            );
          })}

          {leagues.length === 0 && (
            <IonCard><IonCardContent><p style={{ color: "var(--ion-color-medium)" }}>{t("noLeaguesFound")}</p></IonCardContent></IonCard>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AllLeagues;
